import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  endpoints,
  type CompetitorPartner,
  type CompetitorPartnerEntity,
} from "@/lib/api";
import { fmtDate, fmtNumber, platformLabel } from "@/lib/format";
import { Badge, lifecycleVariant, lifecycleLabel } from "@/components/ui/Badge";
import { PlatformBadge } from "@/components/PlatformBadge";
import { Select } from "@/components/ui/Select";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/Table";
import { SectionTabs, DISCOVERY_TABS } from "@/components/SectionTabs";

// ---------------------------------------------------------------------------
// 竞品合作发现 — surfaces KOLs already promoting competitor exchanges.
// Two flavors of value:
//   * lifecycle = untracked / discovered → genuinely new leads to add to
//     the library
//   * lifecycle = evaluating / monitor_only → existing channels that the
//     competition is paying for; high signal for outreach prioritization
// ---------------------------------------------------------------------------

const RANGE_OPTIONS = [
  { value: 30, label: "30 天" },
  { value: 90, label: "90 天" },
  { value: 180, label: "180 天" },
];

// v2.3.4 — bumped from 200 to backend cap (500) so the top-N view doesn't
// silently truncate. When/if we hit 500, we surface a "结果可能被截断" hint
// so the operator knows to narrow the window. Proper pagination would be
// premature — current prod has 29 distinct partners in 90d.
const DISCOVERY_LIMIT = 500;

type Grain = "channel" | "entity";

export default function Discovery() {
  const [days, setDays] = useState(90);
  const [onlyNew, setOnlyNew] = useState(false);
  // v2.8.0 Day 9 — channel-grain is the canonical view operators have
  // been using; entity-grain is the dual-track addition. Default to
  // channel until operators validate entity makes daily work easier.
  const [grain, setGrain] = useState<Grain>("channel");

  const channelQ = useQuery({
    queryKey: ["discovery", "channel", { days }],
    queryFn: () => endpoints.competitorPartners(days, DISCOVERY_LIMIT),
    enabled: grain === "channel",
  });
  const entityQ = useQuery({
    queryKey: ["discovery", "entity", { days }],
    queryFn: () => endpoints.competitorPartnerEntities(days, DISCOVERY_LIMIT),
    enabled: grain === "entity",
  });
  const q = grain === "channel" ? channelQ : entityQ;

  const rows = q.data ?? [];
  const filtered: (CompetitorPartner | CompetitorPartnerEntity)[] = onlyNew
    ? grain === "channel"
      ? (channelQ.data ?? []).filter(
          (r) =>
            r.lifecycle_status === "untracked" ||
            r.lifecycle_status === "discovered",
        )
      : // entity grain: "未收录" filter is approximated as "all channels of
        // this entity are untracked/discovered" — not surfaced by the
        // entity query directly (it aggregates lifecycle_status away).
        // Disable the filter visually in entity mode for now.
        rows
    : rows;
  const possiblyTruncated = rows.length >= DISCOVERY_LIMIT;

  return (
    <div className="h-full overflow-y-auto bg-muted/30">
      <SectionTabs tabs={DISCOVERY_TABS} />
      <div className="border-b border-border bg-background px-6 py-3">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold">竞品合作发现</h1>
          <span className="text-sm text-muted-foreground">
            发现 {rows.length} 条 · 显示 {filtered.length}
            {possiblyTruncated && (
              <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                已达上限 {DISCOVERY_LIMIT};缩短时间窗口可看完整结果
              </span>
            )}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <div className="flex items-center gap-0.5 rounded-md border border-border bg-muted/40 p-0.5 text-xs">
              <GrainPill active={grain === "channel"} onClick={() => setGrain("channel")}>
                频道维度
              </GrainPill>
              <GrainPill active={grain === "entity"} onClick={() => setGrain("entity")}>
                Entity 维度
              </GrainPill>
            </div>
            {grain === "channel" && (
              <label className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  checked={onlyNew}
                  onChange={(e) => setOnlyNew(e.target.checked)}
                />
                只看未收录的（新机会）
              </label>
            )}
            <Select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="w-28"
            >
              {RANGE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </Select>
          </div>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          按推广总数降序。<b>未追踪 / 已发现</b> 状态的频道是"新 KOL 机会"——
          竞品在投但我们还没纳入；已经在 <b>评估中</b> 或 <b>候选</b> 状态的，
          说明对方早已盯上，可以重点跟进评估。
        </p>
      </div>

      <div className="mx-auto max-w-6xl p-6">
        {q.isLoading && <p className="text-sm text-muted-foreground">加载中…</p>}
        {q.isError && (
          <p className="text-sm text-destructive">
            加载失败：{(q.error as Error).message}
          </p>
        )}
        {q.data && filtered.length === 0 && (
          <p className="text-sm text-muted-foreground">
            暂无数据。请等待下一轮内容抓取 + 推广识别完成（约每 15-30 分钟一次），
            或到"系统状态"页手动触发 <code>channel_scan_videos</code> 和{" "}
            <code>sponsorship_detection</code>。
          </p>
        )}
        {filtered.length > 0 && grain === "channel" && (
          <Table>
            <THead>
              <TR>
                <TH>频道</TH>
                <TH>平台</TH>
                <TH>粉丝</TH>
                <TH>状态</TH>
                <TH className="text-right">推广总数 ({days}d)</TH>
                <TH className="text-right">已确认 ({days}d)</TH>
                <TH className="text-right">竞品数 ({days}d)</TH>
                <TH>竞品</TH>
                <TH>最近内容</TH>
              </TR>
            </THead>
            <TBody>
              {(filtered as CompetitorPartner[]).map((r) => (
                <PartnerRow key={r.channel_id} row={r} />
              ))}
            </TBody>
          </Table>
        )}
        {filtered.length > 0 && grain === "entity" && (
          <Table>
            <THead>
              <TR>
                <TH>Entity</TH>
                <TH>平台</TH>
                <TH className="text-right">频道数</TH>
                <TH className="text-right">最大粉丝</TH>
                <TH className="text-right">推广总数 ({days}d)</TH>
                <TH className="text-right">已确认 ({days}d)</TH>
                <TH className="text-right">竞品数 ({days}d)</TH>
                <TH>竞品</TH>
                <TH>最近内容</TH>
              </TR>
            </THead>
            <TBody>
              {(filtered as CompetitorPartnerEntity[]).map((r) => (
                <EntityRow key={r.entity_id} row={r} />
              ))}
            </TBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function GrainPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "rounded bg-background px-2.5 py-1 font-medium text-foreground shadow-sm"
          : "rounded px-2.5 py-1 text-muted-foreground hover:text-foreground"
      }
    >
      {children}
    </button>
  );
}

function EntityRow({ row }: { row: CompetitorPartnerEntity }) {
  return (
    <TR>
      <TD>
        <Link
          to={`/entities/${row.entity_id}`}
          className="font-medium text-primary hover:underline"
        >
          {row.canonical_name}
        </Link>
        {row.is_internal && (
          <span className="ml-1.5 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700">
            ★ 我们
          </span>
        )}
        {row.primary_handle && (
          <div className="text-xs text-muted-foreground">{row.primary_handle}</div>
        )}
      </TD>
      <TD>
        <div className="flex flex-wrap gap-1">
          {row.platforms.map((p) => (
            <span
              key={p}
              className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
            >
              {platformLabel(p)}
            </span>
          ))}
        </div>
      </TD>
      <TD className="text-right tabular-nums">{row.channel_count}</TD>
      <TD className="text-right">{fmtNumber(row.max_follower_count ?? null)}</TD>
      <TD className="text-right font-medium">{row.total_sponsorships}</TD>
      <TD className="text-right text-muted-foreground">{row.confirmed_count}</TD>
      <TD className="text-right">{row.distinct_competitors}</TD>
      <TD>
        <div className="flex flex-wrap gap-1">
          {row.competitors.slice(0, 4).map((c) => (
            <Badge key={c} variant="outline" className="text-[10px]">
              {c}
            </Badge>
          ))}
          {row.competitors.length > 4 && (
            <span className="text-xs text-muted-foreground">
              +{row.competitors.length - 4}
            </span>
          )}
        </div>
      </TD>
      <TD className="text-xs text-muted-foreground">
        {fmtDate(row.latest_video_at)}
      </TD>
    </TR>
  );
}

function PartnerRow({ row }: { row: CompetitorPartner }) {
  return (
    <TR>
      <TD>
        <Link
          to={`/channels/${row.channel_id}`}
          className="font-medium text-primary hover:underline"
        >
          {row.name || row.handle || `#${row.channel_id}`}
        </Link>
        {row.handle && row.name && (
          <div className="text-xs text-muted-foreground">{row.handle}</div>
        )}
      </TD>
      <TD><PlatformBadge platform={row.platform} /></TD>
      <TD>{fmtNumber(row.follower_count ?? null)}</TD>
      <TD>
        <Badge variant={lifecycleVariant(row.lifecycle_status)}>
          {lifecycleLabel(row.lifecycle_status)}
        </Badge>
      </TD>
      <TD className="text-right font-medium">{row.total_sponsorships}</TD>
      <TD className="text-right text-muted-foreground">{row.confirmed_count}</TD>
      <TD className="text-right">{row.distinct_competitors}</TD>
      <TD>
        <div className="flex flex-wrap gap-1">
          {row.competitors.slice(0, 4).map((c) => (
            <Badge key={c} variant="outline" className="text-[10px]">
              {c}
            </Badge>
          ))}
          {row.competitors.length > 4 && (
            <span className="text-xs text-muted-foreground">+{row.competitors.length - 4}</span>
          )}
        </div>
      </TD>
      <TD className="text-xs text-muted-foreground">
        {fmtDate(row.latest_video_at)}
      </TD>
    </TR>
  );
}
